import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import StorageFileLink from "../components/StorageFileLink";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { logActivity } from "../lib/auditLog";
import { buildStorageReference } from "../lib/storageReferences";
import { useToast } from "../contexts/ToastContext";
import {
  CheckCircle2,
  ExternalLink,
  Eye,
  FileText,
  Mail,
  Pencil,
  Phone,
  Plus,
  Search,
  Star,
  Trash2,
  Upload,
  Users,
  Wrench,
  X,
} from "lucide-react";

const vendorsTableGridClass =
  "md:grid-cols-[1fr_0.75fr_0.85fr_0.75fr_0.65fr_0.75fr_0.65fr_7.5rem]";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function buildVendorId(count) {
  return `VEN-${new Date().getFullYear()}-${String(count + 1).padStart(4, "0")}`;
}

function statusClasses(status) {
  if (status === "Active") return "bg-emerald-100 text-emerald-700";
  if (status === "Preferred") return "bg-sky-100 text-sky-700";
  return "bg-slate-200 text-slate-700";
}

function formatMoney(amount, currency) {
  return formatCurrency(Number(amount || 0), currency);
}

const VENDOR_DOCUMENT_BUCKET = "vendor-documents";

function parseFileLinks(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return String(value)
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ name: url.split("/").pop(), url }));
  }
}

function getVendorFilePath(file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  return `vendors/${Date.now()}-${safeName}`;
}

async function uploadVendorFiles(files) {
  const uploadedFiles = [];

  for (const file of files) {
    const path = getVendorFilePath(file);
    const { error } = await supabase.storage
      .from(VENDOR_DOCUMENT_BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) throw error;

    uploadedFiles.push({
      name: file.name,
      url: buildStorageReference(VENDOR_DOCUMENT_BUCKET, path),
      type: file.type,
      size: file.size,
    });
  }

  return uploadedFiles;
}

function VendorModal({ isOpen, vendor, vendorCount, onClose, onSaved, isDark }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    service_category: "Maintenance",
    contact_person: "",
    phone: "",
    email: "",
    company: "",
    status: "Active",
    rating: "4",
    notes: "",
    supporting_files: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({
        name: vendor.name || "",
        service_category: vendor.service_category || "Maintenance",
        contact_person: vendor.contact_person || "",
        phone: vendor.phone || "",
        email: vendor.email || "",
        company: vendor.company || "",
        status: vendor.status || "Active",
        rating: vendor.rating ?? "4",
        notes: vendor.notes || "",
        supporting_files: vendor.supporting_files || "",
      });
    } else {
      setForm({
        name: "",
        service_category: "Maintenance",
        contact_person: "",
        phone: "",
        email: "",
        company: "",
        status: "Active",
        rating: "4",
        notes: "",
        supporting_files: "",
      });
    }
    setSelectedFiles([]);
  }, [vendor, isOpen]);

  if (!isOpen) return null;

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    let uploadedFiles = [];

    try {
      uploadedFiles = selectedFiles.length ? await uploadVendorFiles(selectedFiles) : [];
    } catch (error) {
      setSaving(false);
      console.error("Vendor file upload error:", error);
      toast.error("Vendor file upload failed", "Please check the selected files and try again.");
      return;
    }

    const existingFiles = parseFileLinks(form.supporting_files);
    const supportingFiles = [...existingFiles, ...uploadedFiles];

    const payload = {
      name: form.name,
      service_category: form.service_category,
      contact_person: form.contact_person,
      phone: form.phone,
      email: form.email,
      company: form.company,
      status: form.status,
      rating: Number(form.rating || 0),
      notes: form.notes,
      supporting_files: supportingFiles.length ? JSON.stringify(supportingFiles) : null,
    };

    let result;

    if (vendor?.id) {
      result = await supabase.from("vendors").update(payload).eq("id", vendor.id).select().single();
    } else {
      result = await supabase
        .from("vendors")
        .insert([{ id: buildVendorId(vendorCount), ...payload }])
        .select()
        .single();
    }

    setSaving(false);

    if (result.error) {
      console.error("Save vendor error:", result.error);
      toast.error("Vendor save failed", "Please review the vendor details and try again.");
      return;
    }

    await logActivity({
      action: vendor?.id ? "Updated vendor" : "Created vendor",
      entityType: "vendor",
      entityId: result.data.id,
      entityLabel: result.data.name,
      details: payload,
    });

    toast.success(
      vendor?.id ? "Vendor updated" : "Vendor created",
      `${result.data.name} is now saved in SpaceM8.`
    );
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {vendor?.id ? "Edit Vendor" : "Add Vendor"}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Maintain supplier, contractor, and service-provider contact details.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${isDark ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Input value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Vendor name" className={inputClass} required />
          <Input value={form.company} onChange={(event) => update("company", event.target.value)} placeholder="Company" className={inputClass} />

          <Select value={form.service_category} onChange={(event) => update("service_category", event.target.value)} className={inputClass}>
            <option>Maintenance</option>
            <option>Plumbing</option>
            <option>Electrical</option>
            <option>Cleaning</option>
            <option>Security</option>
            <option>Landscaping</option>
            <option>Legal</option>
            <option>Accounting</option>
            <option>Other</option>
          </Select>

          <Select value={form.status} onChange={(event) => update("status", event.target.value)} className={inputClass}>
            <option>Active</option>
            <option>Preferred</option>
            <option>Inactive</option>
          </Select>

          <Input value={form.contact_person} onChange={(event) => update("contact_person", event.target.value)} placeholder="Contact person" className={inputClass} />
          <Input value={form.phone} onChange={(event) => update("phone", event.target.value)} placeholder="Phone" className={inputClass} />
          <Input type="email" value={form.email} onChange={(event) => update("email", event.target.value)} placeholder="Email" className={inputClass} />
          <Input type="number" min="0" max="5" step="0.5" value={form.rating} onChange={(event) => update("rating", event.target.value)} placeholder="Rating" className={inputClass} />

          <textarea
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Notes"
            className={`min-h-[110px] rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 md:col-span-2 ${inputClass}`}
          />

          <div className={`rounded-2xl border p-4 md:col-span-2 ${isDark ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Vendor documents
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
              className={`block w-full text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Attach contracts, certificates, compliance docs, quotes, invoices, or IDs.
            </p>

            {parseFileLinks(form.supporting_files).length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {parseFileLinks(form.supporting_files).map((file, index) => (
                  <StorageFileLink
                    key={`${file.url}-${index}`}
                    reference={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700"
                  >
                    {file.name || `Document ${index + 1}`}
                  </StorageFileLink>
                ))}
              </div>
            ) : null}

            {selectedFiles.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700"
                  >
                    <Upload className="mr-1 h-3 w-3" />
                    {file.name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button type="button" onClick={onClose} className={`inline-flex h-12 items-center rounded-2xl border px-5 font-medium transition ${isDark ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className={`inline-flex h-12 items-center rounded-2xl px-5 font-medium text-white transition ${saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"}`}>
              {saving ? "Saving..." : "Save Vendor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VendorProfileModal({ isOpen, vendor, metrics, onClose, isDark, currency }) {
  if (!isOpen || !vendor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] border p-6 shadow-2xl ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{vendor.name}</h2>
              <Badge className={statusClasses(vendor.status)}>{vendor.status}</Badge>
            </div>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {vendor.service_category} vendor profile and operational history.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${isDark ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: "Open tickets", value: metrics?.openTickets || 0 },
            { label: "Completed", value: metrics?.completedTickets || 0 },
            { label: "Total spend", value: formatMoney(metrics?.totalSpend || 0, currency) },
            { label: "Rating", value: Number(vendor.rating || 0).toFixed(1) },
          ].map((item) => (
            <div key={item.label} className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
              <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
              <p className={`mt-2 text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{item.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <section className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Maintenance tickets</h3>
            <div className="mt-4 space-y-3">
              {(metrics?.tickets || []).length === 0 ? (
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>No tickets assigned yet.</p>
              ) : (
                metrics.tickets.map((ticket) => (
                  <div key={ticket.id} className={`rounded-2xl border p-3 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{ticket.issue}</p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {ticket.property} - {ticket.priority} - {ticket.status}
                    </p>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Expense history</h3>
            <div className="mt-4 space-y-3">
              {(metrics?.expenses || []).length === 0 ? (
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>No expenses recorded yet.</p>
              ) : (
                metrics.expenses.map((expense) => (
                  <div key={expense.id} className={`rounded-2xl border p-3 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{expense.category}</p>
                        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {expense.property_name} - {expense.status}
                        </p>
                      </div>
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                        {formatMoney(expense.amount || 0, currency)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <section className={`mt-6 rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
          <h3 className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Vendor documents</h3>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {parseFileLinks(vendor.supporting_files).length === 0 ? (
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>No vendor documents uploaded yet.</p>
            ) : (
              parseFileLinks(vendor.supporting_files).map((file, index) => (
                <StorageFileLink
                  key={`${file.url}-${index}`}
                  reference={file.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center justify-between gap-3 rounded-2xl border p-3 transition ${isDark ? "border-slate-800 bg-slate-900 hover:bg-slate-800" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-sky-500" />
                    <span className={`truncate text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {file.name || `Document ${index + 1}`}
                    </span>
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
                </StorageFileLink>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function Vendors({ isDark, setIsDark, currency, setCurrency }) {
  const toast = useToast();
  const [vendors, setVendors] = useState([]);
  const [vendorMetrics, setVendorMetrics] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const fetchVendors = async () => {
    setLoading(true);
    const [
      { data, error },
      { data: ticketData, error: ticketError },
      { data: expenseData, error: expenseError },
    ] = await Promise.all([
      supabase
        .from("vendors")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("maintenance_tickets")
        .select("id, issue, assigned, status, priority, property"),
      supabase
        .from("expenses")
        .select("id, vendor, property_name, amount, status, category"),
    ]);

    if (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
    } else {
      setVendors(data || []);
    }

    if (ticketError) console.error("Error fetching vendor tickets:", ticketError);
    if (expenseError) console.error("Error fetching vendor expenses:", expenseError);

    const metrics = {};

    (data || []).forEach((vendor) => {
      const tickets = (ticketData || []).filter((ticket) => ticket.assigned === vendor.name);
      const expenses = (expenseData || []).filter((expense) => expense.vendor === vendor.name);

      metrics[vendor.name] = {
        tickets,
        expenses,
        openTickets: tickets.filter((ticket) => ticket.status !== "Completed").length,
        completedTickets: tickets.filter((ticket) => ticket.status === "Completed").length,
        totalSpend: expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
      };
    });

    setVendorMetrics(metrics);

    setLoading(false);
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const totals = useMemo(
    () => ({
      total: vendors.length,
      active: vendors.filter((vendor) => vendor.status === "Active").length,
      preferred: vendors.filter((vendor) => vendor.status === "Preferred").length,
      openTickets: Object.values(vendorMetrics).reduce((sum, metric) => sum + metric.openTickets, 0),
      averageRating:
        vendors.length > 0
          ? (vendors.reduce((sum, vendor) => sum + Number(vendor.rating || 0), 0) / vendors.length).toFixed(1)
          : "0.0",
    }),
    [vendors, vendorMetrics]
  );

  const filteredVendors = useMemo(() => {
    return vendors.filter((vendor) => {
      const matchesQuery = `${vendor.name} ${vendor.company} ${vendor.contact_person} ${vendor.service_category} ${vendor.phone} ${vendor.email}`
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesStatus = statusFilter === "All" || vendor.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [vendors, query, statusFilter]);

  const handleDelete = async (vendor) => {
    const confirmed = window.confirm(`Delete vendor ${vendor.name}?`);
    if (!confirmed) return;

    const { error } = await supabase.from("vendors").delete().eq("id", vendor.id);

    if (error) {
      console.error("Delete vendor error:", error);
      toast.error("Vendor deletion failed", "Please try again in a moment.");
      return;
    }

    await logActivity({
      action: "Deleted vendor",
      entityType: "vendor",
      entityId: vendor.id,
      entityLabel: vendor.name,
      details: vendor,
    });

    toast.success("Vendor deleted", `${vendor.name} has been removed from SpaceM8.`);
    fetchVendors();
  };

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Vendor Network"
      title="Vendors"
      description="Manage contractors, suppliers, and service providers used for maintenance and property operations."
      actions={
        <button
          type="button"
          onClick={() => {
            setEditingVendor(null);
            setIsModalOpen(true);
          }}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-orange-500 px-3 text-sm font-medium text-white transition hover:bg-orange-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Vendor
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          { label: "Vendors", value: totals.total, icon: Users, tone: "bg-sky-100 text-sky-700" },
          { label: "Active", value: totals.active, icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700" },
          { label: "Open Tickets", value: totals.openTickets, icon: Wrench, tone: "bg-orange-100 text-orange-700" },
          { label: "Avg Rating", value: totals.averageRating, icon: Star, tone: "bg-amber-100 text-amber-700" },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <h3 className={`mt-3 text-3xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{item.value}</h3>
                </div>
                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search vendors..."
              className={`h-12 pl-11 pr-4 ${isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-800"}`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["All", "Active", "Preferred", "Inactive"].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                  statusFilter === status
                    ? "bg-sky-100 text-sky-700"
                    : isDark
                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={`overflow-hidden rounded-[2rem] border shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className={`hidden items-center gap-4 px-5 py-4 text-sm md:grid ${vendorsTableGridClass} ${isDark ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-600"}`}>
          <span>Vendor</span>
          <span>Category</span>
          <span>Contact</span>
          <span>Workload</span>
          <span>Spend</span>
          <span>Email</span>
          <span>Rating</span>
          <span className="text-center">Actions</span>
        </div>

        {loading ? (
          <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>Loading vendors...</div>
        ) : filteredVendors.length === 0 ? (
          <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>No vendors found.</div>
        ) : (
          filteredVendors.map((vendor) => (
            <div key={vendor.id} className={`grid items-center gap-4 border-t px-5 py-4 text-sm ${vendorsTableGridClass} ${isDark ? "border-slate-800" : "border-slate-200"}`}>
              <div>
                <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{vendor.name}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <p className={isDark ? "text-slate-400" : "text-slate-500"}>{vendor.company || vendor.id}</p>
                  <Badge className={statusClasses(vendor.status)}>{vendor.status}</Badge>
                </div>
              </div>
              <span className={isDark ? "text-slate-300" : "text-slate-600"}>{vendor.service_category}</span>
              <div>
                <p className={isDark ? "text-slate-300" : "text-slate-600"}>{vendor.contact_person || "Not set"}</p>
                <p className={`mt-1 flex items-center gap-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  <Phone className="h-3 w-3" />
                  {vendor.phone || "No phone"}
                </p>
              </div>
              <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                <p>{vendorMetrics[vendor.name]?.openTickets || 0} open</p>
                <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {vendorMetrics[vendor.name]?.completedTickets || 0} completed
                </p>
              </div>
              <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {formatMoney(vendorMetrics[vendor.name]?.totalSpend || 0, currency)}
              </span>
              <p className={`flex items-center gap-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                <Mail className="h-3 w-3" />
                {vendor.email || "No email"}
              </p>
              <span className={isDark ? "text-slate-300" : "text-slate-600"}>{Number(vendor.rating || 0).toFixed(1)}</span>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedVendor(vendor)}
                  className={`rounded-xl border p-2 transition ${isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingVendor(vendor);
                    setIsModalOpen(true);
                  }}
                  className={`rounded-xl border p-2 transition ${isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(vendor)}
                  className={`rounded-xl border p-2 transition ${isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-red-400" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-red-600"}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      <VendorModal
        isOpen={isModalOpen}
        vendor={editingVendor}
        vendorCount={vendors.length}
        onClose={() => {
          setIsModalOpen(false);
          setEditingVendor(null);
        }}
        onSaved={fetchVendors}
        isDark={isDark}
      />

      <VendorProfileModal
        isOpen={Boolean(selectedVendor)}
        vendor={selectedVendor}
        metrics={selectedVendor ? vendorMetrics[selectedVendor.name] : null}
        onClose={() => setSelectedVendor(null)}
        isDark={isDark}
        currency={currency}
      />
    </AppLayout>
  );
}
